import { ApiTableColumn } from '@e3dc-react/shell/Elements/ApiView/ApiView'
import { Moment } from '@e3dc-react/shell/Libs/moment'
import { UnitType } from 'src/Libs/types'
import type { TraceKey } from '../../../system-charts/shared/config/traceKey'
import type { CostSavingChartTraceKey } from 'src/Libs/dynamic-power-prices/cost-saving-chart-trace-keys'

export enum ChartViews {
  Hour = 'hour',
  Day = 'day',
  Week = 'week',
  Month = 'month',
  Year = 'year',
}
export type ChartView = `${ChartViews}`
export type ChartType = 'line' | 'bar' | 'pie' | 'composed' | 'comparison'

export interface SyncModel {
  view: ChartView
  from: Moment
  to: Moment
  customRange: boolean
  showForecast: boolean
}

export type SumKeyOperand = 'add' | 'subtract'

export interface SumKey {
  key: string
  operand: SumKeyOperand
}

export const CUSTOM_UNITS = ['%', '°C']

export interface CustomTrace {
  key: TraceKey
  alternateKey?: string
  name?: string
  color: string
  thickness?: number
  dash?: 'dot' | 'dash'
  opacity?: number
  ids?: number[]
  sumKeys?: SumKey[]
  stackId?: number
  hidden?: boolean
  /** the traces which can be toggled on the chart summary can be set to be off by default */
  offByDefault?: true
  sign?: 1 | -1 //-1 will display as negative values
  type?: 'line' | 'bar' | 'area' //for composed charts
  suffixColors?: TraceSuffixColor[]
  unit?: UnitType
  summaryUnit?: UnitType
}

export interface CustomTraceWithSum extends CustomTrace {
  alternateKey: string
  sumKeys: SumKey[]
}

export interface TraceSuffix {
  name: string
}

export interface TraceSuffixColor {
  traceName: string
  color: string
}

export interface CustomLayout<T = never> {
  id?: number | string
  unit?: UnitType
  minPrefix?: number // minimal unit for function "formatNumberWithSiPrefix" => 1 = k
  isInitial: boolean
  fixed?: boolean
  index?: number
  title?: string
  labelNameKey?: string
  labelTraces?: TraceKey[] | CostSavingChartTraceKey[]
  views: Record<ChartView, ChartType>
  traces: CustomTrace[]
  barmode?: string
  secondUnit?: string
  summaryUnit?: string //unit to display in Summary/Legend if different from standard unit
  hasSummary?: boolean
  summaryMinPrefix?: number // minimal unit for function "formatNumberWithSiPrefix" => 1 = k in Summary/Legend if different from standard unit
  tableProps?: {
    url: string
    title: string
    endpoint?: string
    tableColumns: ApiTableColumn<T>[]
    onClick?: (props: any) => void //eslint-disable-line
  }
  maxHeight?: number
  installationId?: number
  storageSerialNumber?: string
}

export enum FetchingState {
  IsFetching = 0,
  Completed = 1,
}

export interface LoadingState {
  state: FetchingState
  currentMonthTs?: number
  monthAmountToFetch?: number
  fetchedMonths?: number
}

export interface RechartData {
  data?: RechartDataObject[]
  sums?: Record<string, RechartSum>
  loadingState: LoadingState
  error?: string
  hasWeatherBreakthrough?: boolean
  isPocChart?: boolean
}

export type RechartDataObject = Record<string, number | string | boolean> & DataWithTime

export interface RechartSum {
  history: number | null
  forecast?: number | null
  // customTrace?: CustomTrace
  customColor?: string
}

export type RechartDataRow = Record<string, number | string | boolean>

export type DataWithTime = { ts?: number | string } | { time?: number | string }
